/* eslint-disable react/prop-types */
import React, { useRef } from 'react';
import {
  Modal,
  Slide,
  Paper,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import DetailRow from './DetailRow';
import StatusBadge from './StatusBadge';
import RequestActions from './RequestActions';

const DRAWER_WIDTH = 420;

// ── Section Header ──────────────────────────────────────────────────────────
function SectionHeader({ label }) {
  return (
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: '12px',
        color: '#637381',
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        mt: 2,
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
  );
}

// ── Info Note Box ────────────────────────────────────────────────────────────
function NoteBox({ text }) {
  return (
    <Box
      sx={{
        mt: 2,
        mb: 1,
        px: 2,
        py: 1.5,
        backgroundColor: '#FFF8E1',
        borderRadius: '10px',
        border: '1px solid #FFE082',
      }}
    >
      <Typography sx={{ fontSize: '12.5px', color: '#7B5800', lineHeight: 1.7 }}>
        {text}
      </Typography>
    </Box>
  );
}

// ── Date Formatter ──────────────────────────────────────────────────────────
const AR_MONTHS = [
  'يناير','فبراير','مارس','أبريل','مايو','يونيو',
  'يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر',
];
const EN_MONTHS = [
  'Jan','Feb','Mar','Apr','May','Jun',
  'Jul','Aug','Sep','Oct','Nov','Dec',
];

/**
 * Parses API date string "DD-MM-YYYY HH:mm:ss AM/PM"
 * Returns formatted string like "30 أغسطس 2026 • 02:17 م"
 */
function formatDate(dateStr, isRtl) {
  if (!dateStr) return null;
  try {
    // e.g. "30-08-2026 02:17:51 PM"
    const [datePart, timePart, meridiem] = dateStr.trim().split(' ');
    const [day, month, year] = datePart.split('-');
    const [hour, minute] = timePart.split(':');
    const monthIndex = parseInt(month, 10) - 1;

    const monthLabel = isRtl ? AR_MONTHS[monthIndex] : EN_MONTHS[monthIndex];
    const meridiemLabel = meridiem
      ? isRtl
        ? meridiem === 'AM' ? 'ص' : 'م'
        : meridiem
      : '';

    return `${parseInt(day, 10)} ${monthLabel} ${year} • ${hour}:${minute} ${meridiemLabel}`.trim();
  } catch {
    return dateStr;
  }
}

export default function RequestDetailDrawer({
  open,
  onClose,
  row,
  tabValue,
  onApproveClick,
  onRejectClick,
  onMarkSoldClick,
  onMarkNotSoldClick,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  // Keep the last valid row in memory so content stays visible during exit animation
  const lastRowRef = useRef(null);
  if (row) lastRowRef.current = row;
  const displayRow = row || lastRowRef.current;

  if (!displayRow) return null;

  const {
    fullNameAr,
    fullNameEn,
    creationDate,
    requestStatus,
    flagNameEn,
    flagNameAr,
    listingType,
    userPhone,
    flagDetails,
    categoryDetails,
  } = displayRow;

  const currentListingType = listingType || 'SALE';
  const isRent = currentListingType === 'RENT';
  const isOffPlan = categoryDetails?.saleType === 'OFF_PLAN';

  // ── Labels ────────────────────────────────────────────────────────────────
  const requestTypeLabel = isRtl
    ? isRent
      ? 'طلب إيجار'
      : 'طلب حجز'
    : isRent
      ? 'Rent Request'
      : 'Reservation Request';

  const unitName = isRtl
    ? flagNameAr || t('General.noData')
    : flagNameEn || t('General.noData');

  const unitType = flagDetails?.type
    ? t(`${flagDetails.type}`)
    : t('General.noData');

  const purposeLabel = isRtl
    ? isRent
      ? 'إيجار'
      : 'بيع (محسوب من نوع الوحدة)'
    : isRent
      ? 'Rent'
      : 'Sale (from unit type)';

  const customerName = isRtl
    ? fullNameAr || t('General.noData')
    : fullNameEn || t('General.noData');

  const isExpired = requestStatus === 'EXPIRED';
  const isCancelled = requestStatus === 'CANCELLED';
  const noActions = isExpired || isCancelled;

  const projectName = isRtl
    ? categoryDetails?.nameAr
    : categoryDetails?.nameEn;

  return (
    <Modal
      open={open}
      onClose={onClose}
      keepMounted
      sx={{ zIndex: 1300 }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0,0,0,0.4)',
            transition: 'opacity 350ms ease !important',
          },
        },
      }}
    >
      <Slide
        direction={isRtl ? 'right' : 'left'}
        in={open}
        timeout={{ enter: 420, exit: 300 }}
        easing={{
          enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
          exit: 'cubic-bezier(0.7, 0, 0.84, 0)',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            top: 0,
            [isRtl ? 'left' : 'right']: 0,
            height: '100%',
            width: { xs: '100%', sm: DRAWER_WIDTH },
            borderRadius: isRtl ? '0 16px 16px 0' : '16px 0 0 16px',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
            backgroundColor: '#F9FAFB',
            display: 'flex',
            flexDirection: 'column',
            outline: 'none',
            overflow: 'hidden',
          }}
        >
          {/* ── Header ── */}
          <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #F0F2F5' }}>
            {/* Close button row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              {/* Request type chip */}
              <Chip
                label={requestTypeLabel}
                size="small"
                sx={{
                  backgroundColor: isRent ? '#E8F4FD' : '#FFF3E0',
                  color: isRent ? '#1565C0' : '#E65100',
                  fontWeight: 700,
                  fontSize: '11.5px',
                  borderRadius: '6px',
                  height: '24px',
                }}
              />
              <IconButton onClick={onClose} size="small">
                <CloseIcon sx={{ fontSize: '20px', color: '#8D9AA5' }} />
              </IconButton>
            </Box>

            {/* Unit / Flag name as title */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '18px',
                color: '#212B36',
                lineHeight: 1.3,
              }}
            >
              {unitName}
            </Typography>
          </Box>

          {/* ── Scrollable body ── */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 2 }}>
            {/* ── Status ── */}
            <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
              <StatusBadge requestStatus={requestStatus} tabValue={tabValue} />
            </Box>

            <Divider sx={{ borderColor: '#F0F2F5', my: 2 }} />

            {/* ── Client Section ── */}
            <SectionHeader label={isRtl ? 'العميل' : 'Client'} />

            <DetailRow
              label={isRtl ? 'الاسم' : 'Name'}
              value={customerName}
            />
            <DetailRow
              label={isRtl ? 'الجوال' : 'Phone'}
              value={userPhone || t('General.noData')}
            />
            <DetailRow
              label={isRtl ? 'نوع الوحدة' : 'Unit Type'}
              value={unitType}
            />
            <DetailRow
              label={isRtl ? 'الغرض' : 'Purpose'}
              value={purposeLabel}
            />
            <DetailRow
              label={isRtl ? 'تاريخ الطلب' : 'Request Date'}
              // value={formatDate(creationDate, isRtl) || t('General.noData')}
                value={creationDate || t('General.noData')}
              noDivider
            />

            {/* ── Off-Plan Project Section ── */}
            {isOffPlan && categoryDetails && (
              <>
                <Divider sx={{ borderColor: '#F0F2F5', my: 2 }} />
                <SectionHeader label={isRtl ? 'المشروع' : 'Project'} />

                {projectName && (
                  <DetailRow
                    label={isRtl ? 'اسم المشروع' : 'Project Name'}
                    value={projectName}
                  />
                )}
                <DetailRow
                  label={isRtl ? 'نسبة الإنجاز' : 'Completion'}
                  value={
                    categoryDetails.completionPercentage
                      ? `${categoryDetails.completionPercentage}%`
                      : t('General.noData')
                  }
                />
                <DetailRow
                  label={isRtl ? 'المرحلة الحالية' : 'Current Stage'}
                  value={categoryDetails.currentStage || t('General.noData')}
                />
                {categoryDetails.wafiLicenceEndDate && (
                  <DetailRow
                    label={isRtl ? 'رخصة وافي' : 'WAFI Licence'}
                    value={`WAFI — ${isRtl ? 'تنتهي' : 'ends'} ${categoryDetails.wafiLicenceEndDate}`}
                  />
                )}
                {categoryDetails.expectedDeliveryDate && (
                  <DetailRow
                    label={isRtl ? 'تاريخ التسليم المتوقع' : 'Expected Delivery'}
                    value={categoryDetails.expectedDeliveryDate}
                    noDivider
                  />
                )}

                {/* Off-plan note */}
                <NoteBox
                  text={
                    isRtl
                      ? 'الحجز المبدئي بدون دفع. التأكيد لا ينقل الملكية — يُستكمل العقد النظامي عبر المطوّر وحساب الضمان.'
                      : 'Preliminary reservation without payment. Confirmation does not transfer ownership — the formal contract is completed via the developer and escrow account.'
                  }
                />
              </>
            )}
          </Box>

          {/* ── Actions footer ── */}
          <Box sx={{ px: 3, py: 2.5, borderTop: '1px solid #F0F2F5' }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '14px',
                color: '#1A2233',
                mb: 1.5,
              }}
            >
              {isRtl ? 'الإجراء' : 'Action'}
            </Typography>

            {noActions ? (
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  backgroundColor: '#F5F5F5',
                  borderRadius: '10px',
                  border: '1px solid #E0E0E0',
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: '13px', color: '#9E9E9E', fontWeight: 500 }}>
                  {isExpired
                    ? isRtl
                      ? 'لا توجد إجراءات على طلب منتهي الصلاحية.'
                      : 'No actions available for an expired request.'
                    : isRtl
                      ? 'لا توجد إجراءات على طلب ملغي.'
                      : 'No actions available for a cancelled request.'}
                </Typography>
              </Box>
            ) : (
              <RequestActions
                row={displayRow}
                tabValue={tabValue}
                onApproveClick={onApproveClick}
                onRejectClick={onRejectClick}
                onMarkSoldClick={onMarkSoldClick}
                onMarkNotSoldClick={onMarkNotSoldClick}
              />
            )}
          </Box>
        </Paper>
      </Slide>
    </Modal>
  );
}
